import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, ShieldCheck, Clock, Sparkles, CreditCard, Headphones } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProductCard, { ProductCardData } from "@/components/ProductCard";
import { fetchProductsWithStock } from "@/lib/products";
import { supabase } from "@/integrations/supabase/client";
import { useApp } from "@/hooks/useApp";
import Seo from "@/components/Seo";

// Below-the-fold (testimonials + trust + FAQ) is split into a separate chunk
// loaded after first paint to keep TBT/LCP small.
const HomeBelowFold = lazy(() => import("@/components/HomeBelowFold"));

const faqsPt = [
  { q: "O que é a NickRobotic?", a: "Marketplace digital com entrega automática imediata: seguidores e curtidas para Instagram, TikTok e YouTube, contas verificadas, painel SMM com API e produtos digitais. Você compra, paga no PIX ou cartão e recebe em segundos — sem falar com ninguém." },
  { q: "Como funciona a entrega automática?", a: "Confirmado o pagamento (PIX em segundos, cartão via Stripe), o sistema libera o ativo na hora. Produtos digitais aparecem em Meus Pedidos por 30 dias; serviços SMM começam em minutos e você acompanha o status em Meus Serviços." },
  { q: "Quais formas de pagamento são aceitas?", a: "PIX com confirmação automática, cartão de crédito/débito via Stripe e o saldo da sua carteira NickRobotic. A carteira em reais permite recarregar uma vez e fechar várias compras sem reentrar dados de cartão." },
  { q: "É seguro comprar seguidores Instagram aqui?", a: "Sim. Trabalhamos com provedores curados, entrega em curva natural (drip-feed) em vários serviços, refill automático em serviços elegíveis e nunca pedimos sua senha — só o link público do alvo. Cada pedido tem status em tempo real." },
  { q: "Em quanto tempo recebo meu pedido?", a: "Produtos digitais e contas: entrega instantânea em Meus Pedidos. Serviços SMM (seguidores, curtidas, views): início em minutos e conclusão no prazo informado em cada card de serviço — normalmente em até 24h." },
  { q: "Existe garantia ou refill?", a: "Sim. Serviços marcados com a etiqueta refill repõem automaticamente quedas dentro do prazo. Para produtos digitais, abra ticket pela página de Suporte em até 7 dias após a compra — o time devolve o valor à carteira ou reprocessa o pedido." },
  { q: "Posso fazer pedidos em massa para vários links?", a: "Sim. Cada serviço tem a aba Pedido em massa: cole até centenas de links (com quantidades opcionais por linha) ou importe um CSV/TXT. Possui pausar, cancelar e download CSV do resultado." },
  { q: "Preciso fornecer minha senha do Instagram?", a: "Nunca. Para seguidores, curtidas e views basta o link público do perfil, post ou vídeo. Nenhum serviço da plataforma exige senha." },
  { q: "Quanto custa comprar 1000 seguidores no Instagram?", a: "Depende do tipo (brasileiros reais com foto, premium, internacionais) e começa a partir de poucos reais por mil. Veja preços ao vivo na página de Serviços — sem assinatura, você paga só pelo pedido." },
  { q: "Os seguidores são brasileiros ou estrangeiros?", a: "Os dois. Temos pacotes específicos de seguidores Instagram brasileiros, contas reais com foto e bio, e também opções internacionais para volume com menor custo. Cada serviço indica claramente a origem." },
  { q: "Vocês têm painel SMM com API para revendedores?", a: "Sim. Gere sua chave em Minha Conta → API e dispare pedidos programaticamente, com webhooks de status e consulta de saldo da carteira. Endpoints REST compatíveis com o padrão SMM (add/status/services/balance)." },
  { q: "Como funciona o comprovante e o reembolso?", a: "O comprovante vai para o seu e-mail logo após o pagamento e fica visível em Meus Pedidos por 30 dias (com PDF para download). Reembolsos retornam à sua carteira NickRobotic, prontos para reusar em qualquer compra." },
];

const faqsEn = [
  { q: "What is NickRobotic?", a: "Digital marketplace with instant automatic delivery: Instagram/TikTok/YouTube followers and likes, verified accounts, SMM panel with API and digital products. Buy, pay with PIX or card, receive in seconds — no human in the loop." },
  { q: "How does automatic delivery work?", a: "Once payment is confirmed (PIX in seconds, card via Stripe), the asset is released instantly. Digital products show up in My Orders for 30 days; SMM services start within minutes and you track them live in My Services." },
  { q: "Which payment methods do you accept?", a: "PIX with automatic confirmation, credit/debit card via Stripe, and your NickRobotic wallet balance. The wallet lets you top up once and check out repeatedly without re-entering card data." },
  { q: "Is it safe to buy Instagram followers here?", a: "Yes. Curated providers, drip-feed natural delivery on many services, auto-refill on eligible ones, and we never ask for your password — only the public link of the target. Every order has live status." },
  { q: "How fast will I receive my order?", a: "Digital products and accounts: instant in My Orders. SMM services (followers, likes, views): start within minutes and complete within the time shown on each service card — typically inside 24h." },
  { q: "Do you offer guarantee or refill?", a: "Yes. Services flagged with refill auto-replenish drops within the warranty window. For digital products, open a Support ticket within 7 days of purchase — the team refunds the wallet or reprocesses the order." },
  { q: "Can I place mass orders for multiple links?", a: "Yes. Every service has a Mass order tab: paste hundreds of links (with optional per-line quantity) or import a CSV/TXT. Includes pause, cancel and CSV download of the results." },
  { q: "Do I need to share my Instagram password?", a: "Never. For followers, likes and views we only need the public link to the profile, post or video. No service on the platform requires your password." },
  { q: "How much does 1000 Instagram followers cost?", a: "Depends on the type (real Brazilian with photo, premium, international) and starts at a few dollars per thousand. Live pricing is on the Services page — no subscription, pay per order." },
  { q: "Are the followers real or international?", a: "Both. We have real Brazilian profiles (photo + bio), premium tiers, and high-volume international options for cheaper boosts. Each service clearly states the source." },
  { q: "Do you offer an SMM panel API for resellers?", a: "Yes. Create a key under My Account → API and trigger orders programmatically, with status webhooks and wallet balance lookups. REST endpoints compatible with the standard SMM panel format (add/status/services/balance)." },
  { q: "How do receipts and refunds work?", a: "The receipt is emailed right after payment and stays in My Orders for 30 days (downloadable PDF). Refunds go back to your NickRobotic wallet, ready to reuse on any purchase." },
];

const catalogPt = [
  { title: "Seguidores Instagram brasileiros", desc: "Perfis reais, com foto e bio, entregues de forma gradual.", href: "/seguidores-reais-instagram" },
  { title: "Seguidores Instagram barato", desc: "Pacotes a partir de poucos reais, ideais para começar.", href: "/seguidores-instagram-barato" },
  { title: "Curtidas no Instagram", desc: "Curtidas reais em posts, reels e vídeos para engajamento.", href: "/curtidas-instagram" },
  { title: "Impulsionar Instagram", desc: "Combo de seguidores, curtidas e visualizações.", href: "/impulsionar-instagram" },
  { title: "Seguidores TikTok", desc: "Cresça no TikTok com entrega automática e refill.", href: "/seguidores-tiktok" },
  { title: "Inscritos no YouTube", desc: "Inscritos reais para destravar monetização e prova social.", href: "/comprar-inscritos-youtube" },
  { title: "Visualizações YouTube", desc: "Views de alta retenção para vídeos novos e antigos.", href: "/comprar-visualizacoes-youtube" },
  { title: "Painel SMM completo", desc: "Catálogo completo: Instagram, TikTok, YouTube e mais.", href: "/painel-smm" },
];
const catalogEn = [
  { title: "Real Brazilian Instagram followers", desc: "Real profiles with photo and bio, delivered gradually.", href: "/seguidores-reais-instagram" },
  { title: "Cheap Instagram followers", desc: "Packages starting at a few dollars — perfect to get going.", href: "/seguidores-instagram-barato" },
  { title: "Instagram likes", desc: "Real likes on posts, reels and videos to boost engagement.", href: "/curtidas-instagram" },
  { title: "Boost Instagram", desc: "Followers + likes + views combo packages.", href: "/impulsionar-instagram" },
  { title: "TikTok followers", desc: "Grow on TikTok with auto delivery and refill.", href: "/seguidores-tiktok" },
  { title: "YouTube subscribers", desc: "Real subscribers to unlock monetization and social proof.", href: "/comprar-inscritos-youtube" },
  { title: "YouTube views", desc: "High-retention views for new or old videos.", href: "/comprar-visualizacoes-youtube" },
  { title: "Full SMM panel", desc: "Full catalog: Instagram, TikTok, YouTube and more.", href: "/painel-smm" },
];

export default function Home() {
  const { t, lang } = useApp();
  const [featured, setFeatured] = useState<ProductCardData[]>([]);
  const [showBelowFold, setShowBelowFold] = useState(false);

  useEffect(() => {
    const w: any = window;
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      // Fetch a wider pool, then pick top product per category and fill by rank.
      Promise.all([
        fetchProductsWithStock(60),
        // Paid landing slots take absolute priority over the admin pin
        supabase
          .from("feature_landing_slots")
          .select("product_id, paid_amount, ends_at")
          .eq("status", "active")
          .gt("ends_at", new Date().toISOString())
          .order("paid_amount", { ascending: false }),
      ])
        .then(([all, slotsRes]) => {
          if (cancelled) return;
          const MAX = 6;
          const sorted = [...all].sort(
            (a, b) => (b.rank_score ?? 0) - (a.rank_score ?? 0) || b.sales_count - a.sales_count,
          );
          const picked: ProductCardData[] = [];
          const usedIds = new Set<string>();
          const usedCats = new Set<string>();

          // 0) Paid landing slots (highest priority)
          const slotIds = ((slotsRes as any)?.data ?? []).map((s: any) => s.product_id);
          for (const sid of slotIds) {
            if (picked.length >= MAX) break;
            const p = sorted.find((x) => x.id === sid);
            if (!p || usedIds.has(p.id)) continue;
            usedIds.add(p.id);
            usedCats.add(p.custom_category_id || p.category || "__none__");
            picked.push(p);
          }
          // 1) admin-pinned "Página principal" products, by rank
          for (const p of sorted) {
            if (picked.length >= MAX) break;
            if (!p.home_page) continue;
            if (usedIds.has(p.id)) continue;
            usedIds.add(p.id);
            const catKey = p.custom_category_id || p.category || "__none__";
            usedCats.add(catKey);
            picked.push(p);
          }
          // 2) best of each remaining category — they compete by rank for the leftover slots
          for (const p of sorted) {
            if (picked.length >= MAX) break;
            if (usedIds.has(p.id)) continue;
            const catKey = p.custom_category_id || p.category || "__none__";
            if (usedCats.has(catKey)) continue;
            usedCats.add(catKey);
            usedIds.add(p.id);
            picked.push(p);
          }
          // 3) fill remaining slots with next best by rank
          for (const p of sorted) {
            if (picked.length >= MAX) break;
            if (usedIds.has(p.id)) continue;
            picked.push(p);
          }
          setFeatured(picked);
        })
        .catch(() => {});
    };
    let idleId: any;
    let timeoutId: any;
    if (typeof w.requestIdleCallback === "function") {
      idleId = w.requestIdleCallback(run, { timeout: 2500 });
    } else {
      timeoutId = setTimeout(run, 1000);
    }
    return () => {
      cancelled = true;
      if (idleId && w.cancelIdleCallback) w.cancelIdleCallback(idleId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);


  // Defer below-the-fold render (and its data fetches) until the browser is idle,
  // so it never competes with the initial paint. IntersectionObserver gates
  // it on actual scroll proximity (mobile especially); falls back to idle.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (showBelowFold) return;
    const trigger = () => setShowBelowFold(true);
    const w: any = window;
    let io: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== "undefined" && sentinelRef.current) {
      io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) trigger();
      }, { rootMargin: "600px 0px" });
      io.observe(sentinelRef.current);
    }
    let idleId: any, timeoutId: any;
    if (typeof w.requestIdleCallback === "function") {
      idleId = w.requestIdleCallback(trigger, { timeout: 5000 });
    } else {
      timeoutId = setTimeout(trigger, 3000);
    }
    return () => {
      io?.disconnect();
      if (idleId && w.cancelIdleCallback) w.cancelIdleCallback(idleId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [showBelowFold]);

  const faqs = lang === "en" ? faqsEn : faqsPt;

  const benefits = useMemo(() => [
    { icon: Zap, title: t("bf_instant"), desc: t("bf_instant_d") },
    { icon: ShieldCheck, title: t("bf_secure"), desc: t("bf_secure_d") },
    { icon: Clock, title: t("bf_access"), desc: t("bf_access_d") },
  ], [t]);

  const steps = useMemo(() => (lang === "en"
    ? [
        { icon: Sparkles, title: "Choose your asset", desc: "Browse our catalog of Instagram services, accounts and digital products." },
        { icon: CreditCard, title: "Pay with PIX or card", desc: "Confirmation happens in seconds via PIX or Stripe checkout." },
        { icon: Zap, title: "Receive automatically", desc: "Credentials and orders are delivered instantly to your account." },
        { icon: Headphones, title: "Track and get support", desc: "Follow every order in real time and talk to support whenever you need." },
      ]
    : [
        { icon: Sparkles, title: "Escolha o ativo", desc: "Navegue pelo catálogo de serviços Instagram, contas e produtos digitais." },
        { icon: CreditCard, title: "Pague no PIX ou cartão", desc: "Confirmação em segundos via PIX ou checkout Stripe." },
        { icon: Zap, title: "Receba automaticamente", desc: "Credenciais e pedidos entregues na hora, direto na sua conta." },
        { icon: Headphones, title: "Acompanhe com suporte", desc: "Veja cada pedido em tempo real e fale com o suporte quando precisar." },
      ]), [lang]);

  const catalog = lang === "en" ? catalogEn : catalogPt;

  const faqJsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  }), [faqs]);

  const itemListJsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: catalog.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.title,
      url: `https://nickrobotic.com${c.href}`,
    })),
  }), [catalog]);

  const breadcrumbJsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: lang === "en" ? "Home" : "Início", item: "https://nickrobotic.com/" },
    ],
  }), [lang]);

  return (
    <>
      <Seo
        title={lang === "en"
          ? "Buy Instagram Followers & Real Accounts | NickRobotic"
          : "Comprar Seguidores Instagram Brasileiros | NickRobotic"}
        description={lang === "en"
          ? "Buy real Instagram followers, likes and verified accounts with instant automatic delivery. PIX or card, refill guarantee, 24/7 support."
          : "Comprar seguidores Instagram brasileiros e reais com entrega automática em segundos. PIX ou cartão, garantia de reposição e suporte humano 24h."}
        path="/"
        jsonLd={[faqJsonLd, itemListJsonLd, breadcrumbJsonLd]}
      />


      {/* Hero — pure CSS background (no image request) so the LCP element is the H1 text. */}
      <section
        className="relative overflow-hidden hero-bg"
        style={{ background: "var(--gradient-hero)" }}
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{
            background:
              "radial-gradient(60% 50% at 20% 30%, hsl(320 100% 60% / 0.18), transparent 60%), radial-gradient(50% 45% at 80% 70%, hsl(190 100% 55% / 0.16), transparent 60%)",
          }}
        />
        <div className="container relative py-24 md:py-32 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs mb-6">
            <Sparkles className="w-3 h-3" /> {t("hero_badge")}
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-4 max-w-3xl mx-auto leading-tight">
            <span className="gradient-text">{t("hero_title_a")}</span> {t("hero_title_b")}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-4">{t("hero_sub")}</p>
          <p className="text-base text-muted-foreground/90 max-w-2xl mx-auto mb-8">
            {lang === "en"
              ? "Real Instagram followers, likes, views and verified accounts — paid by card or PIX, delivered automatically in seconds. No password required, refill guarantee and human 24/7 support."
              : "Seguidores Instagram brasileiros, curtidas, visualizações e contas verificadas — pague no PIX ou cartão e receba automaticamente em segundos. Sem senha, garantia de reposição e suporte humano 24h."}
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg"><Link to="/produtos">{t("hero_cta")} <ArrowRight className="w-4 h-4 ml-1" /></Link></Button>
            <Button asChild size="lg" variant="outline"><Link to="/servicos">{lang === "en" ? "Instagram services" : "Serviços Instagram"}</Link></Button>
          </div>
        </div>
      </section>

      <section className="container py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {benefits.map((b) => (
            <div key={b.title} className="glow-card rounded-xl p-6 border border-border">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ background: "var(--gradient-primary)" }}>
                <b.icon className="w-6 h-6 text-primary-foreground" />
              </div>
              <p className="font-semibold text-lg mb-1">{b.title}</p>
              <p className="text-sm text-muted-foreground">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container py-16" aria-labelledby="catalog-heading">
        <div className="max-w-2xl mx-auto text-center mb-10">
          <h2 id="catalog-heading" className="text-3xl font-bold mb-2">
            {lang === "en" ? "Instagram, TikTok & YouTube services" : "Serviços de Instagram, TikTok e YouTube"}
          </h2>
          <p className="text-muted-foreground">
            {lang === "en"
              ? "Pick the exact growth package you need — every link below opens a dedicated page with pricing and details."
              : "Escolha o pacote certo para o seu objetivo — cada link abaixo abre uma página com preços e detalhes."}
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {catalog.map((c) => (
            <Link
              key={c.href}
              to={c.href}
              aria-label={lang === "en" ? `View details: ${c.title}` : `Ver detalhes: ${c.title}`}
              className="glow-card rounded-xl p-5 border border-border hover:border-primary/50 transition-colors block"
            >
              <h3 className="font-semibold mb-1 text-sm">{c.title}</h3>
              <p className="text-xs text-muted-foreground">{c.desc}</p>
              <span aria-hidden="true" className="inline-flex items-center text-primary text-xs mt-3">
                <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="container py-16">

        <div className="max-w-2xl mx-auto text-center mb-10">
          <h2 className="text-3xl font-bold mb-2">{lang === "en" ? "How it works" : "Como funciona a entrega automática"}</h2>
          <p className="text-muted-foreground">
            {lang === "en"
              ? "Four steps from cart to delivery — no waiting, no manual approvals."
              : "Quatro passos do carrinho à entrega — sem espera, sem aprovação manual."}
          </p>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          {steps.map((s, i) => (
            <div key={s.title} className="glow-card rounded-xl p-5 border border-border relative">
              <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center">{i + 1}</div>
              <s.icon className="w-6 h-6 text-primary mb-3" aria-hidden="true" />
              <p className="font-semibold mb-1">{s.title}</p>
              <p className="text-sm text-muted-foreground">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold mb-1">{t("featured")}</h2>
            <p className="text-muted-foreground">{t("featured_sub")}</p>
          </div>
          <Button asChild variant="ghost"><Link to="/produtos">{lang === "en" ? "See all products" : "Ver todos os produtos"} <ArrowRight className="w-4 h-4 ml-1" /></Link></Button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((p) => <ProductCard key={p.id} p={p} variant="home" />)}
        </div>

      </section>

      <div ref={sentinelRef} aria-hidden="true" />
      {showBelowFold && (
        <Suspense fallback={null}>
          <HomeBelowFold lang={lang} t={t} faqs={faqs} />
        </Suspense>
      )}
    </>
  );
}
